import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import { setRowData } from '../../../../Reducer/GridReducer';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import Global from '../../../../Services/Common/Global';
import useSearchMutaion from '../../../../hooks/apis/useSearchMutaion';
import useDeleteMutation from '../../../../hooks/apis/useDeleteMutaion';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../util';
import { COM_BASIC_Schema } from './COM_BASIC_Schema';
import { MEM_BASIC_Schema } from '../../Member/MEM_BASIC/MEM_BASIC_Schema';
import dayjs from 'dayjs';

/** 조회 행: paymentDay 우선, 없으면 구 paymentDate에서 매월 일자 추출 */
const MapCompanyRowPaymentDay = (row) => {
  if (!row || typeof row !== 'object') return row;
  const raw =
    row.paymentDay ?? row.PaymentDay ?? row.payment_day ?? row.PAYMENT_DAY;
  if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
    const n = Math.min(31, Math.max(1, Number(String(raw).trim())));
    if (Number.isFinite(n)) return { ...row, paymentDay: n };
  }
  const legacy = row.paymentDate ?? row.PaymentDate;
  if (legacy) {
    const parsed = dayjs(legacy);
    if (parsed.isValid()) return { ...row, paymentDay: parsed.date() };
  }
  return { ...row, paymentDay: '' };
};

/**
 * 회사(Company) 기본정보 관련 React Query 훅 모음
 * - useSearch1: 회사 목록 조회 (GetComBasicR1)
 * - useSearch2: 대표자 목록 조회 (GetComBasicR2)
 * - useSave1 / useDelete1: 저장·삭제
 */
export const useSearch1 = (options = {}) => {
  const dispatch = useDispatch();
  const { onSuccess: onSuccessOption } = options;
  const params = mapParamsBySchema(COM_BASIC_Schema.mapper.search1);

  const query = useQuery({
    queryKey: ['company', 'getComBasicR1', params],
    queryFn: () =>
      apiRequest('post', Global.urls.Company.getComBasicR1, params),
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess) return;

    handleQuerySuccess(query.data, {
      onSuccess: (rows) => {
        const list = Array.isArray(rows)
          ? rows.map(MapCompanyRowPaymentDay)
          : rows;
        dispatch(setRowData({ gridId: 'grid1', rowData: list }));
        onSuccessOption?.(list);
      },
    });
  }, [query.isSuccess, query.dataUpdatedAt, dispatch]);

  return query;
};

export const useSearch2 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      COM_BASIC_Schema.mapper.search2,
      context
    );
    return apiRequest(
      'post',
      Global.urls.Company.getComBasicR2,
      encryptedParams
    );
  }, successOptions);
};

export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, COM_BASIC_Schema.mapper.delete1);
    return apiRequest('post1', Global.urls.Company.deleteComBasicD1, payload);
  }, options);
};

export const useSave1 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, COM_BASIC_Schema.mapper.save1);
    return apiRequest('post1', Global.urls.Company.saveComBasicU1, payload);
  }, options);
};
export const useSave2 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, MEM_BASIC_Schema.mapper.save1, {
      companyInfo: options.companyInfo ?? [],
      authCodeList: options.authCodeList ?? [],

      getProperName: ({ userInfo, search, companyInfo }) => {
        const coCd =
          userInfo?.authCd === 'ADMIN' ? search?.coCd : userInfo?.coCd;

        if (userInfo?.authCd === 'ADMIN') {
          const selected = (companyInfo ?? []).find((c) => c.coCd === coCd);
          return selected?.properName || '';
        }
        return userInfo?.properName || '';
      },

      getAuthCd: (authNm, { authCodeList }) => {
        const name = authNm || '근로자';
        return (authCodeList ?? []).find((f) => f.name === name)?.value || '';
      },
    });

    return apiRequest('post1', Global.urls.Member.saveMemBasicU1, payload);
  }, options);
};
